import {
  CalendarIcon,
  StackIcon,
  SyncIcon,
  UploadIcon,
} from "@primer/octicons-react";
import { ActionList, Box, Heading, Label, useConfirm } from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/ui/button";
import Text from "../../../components/ui/text";
import MyTextInput from "../../../components/ui/text-input/TextInput";
import MyText from "../../../components/ui/text/Text";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { foCaAnApi } from "../../../api/foCaAnApi";
import { foMonAnChiTietApi } from "../../../api/foMonAnChiTietApi";
import { foThucDonApi } from "../../../api/foThucDonApi";
import { foCaAnTruongApi } from "../../../api/foCaAnTruongApi";
import ComboboxMonAn from "../../../components/combobox-monan";
import { NotifyHelper } from "../../../helpers/toast";
import { RootState } from "../../../state/reducers";
import ThucDonImport from "./import-thucdon";
import ThucDonEditModal from "./ThucDonEditModal";
import { LabelColorOptions } from "@primer/react/lib/Label";
import { ComboboxTruong } from "../../../components/combobox-truong";

// Interfaces
interface IFoThucDon {
  id: number;
  fo_caan_ngay_id: number;
  fo_monan_chitiet_id: number;
  calo: number;
  ghi_chu: string;
}

interface IFoThucDonVm extends IFoThucDon {
  ngay_an: Date;
  dm_truong_id: number;
  ten_truong: string;
  fo_caan_id: number;
  ca_an: string;
  ten_mon_an: string;
  dm_coso_id: number;
}

interface IFoThucDonTheoNgayVm {
  ngay_ans: INgayAn[];
}

interface INgayAn {
  ngay_an: Date;
  ca_an: ICaAn[];
}

interface ICaAn {
  fo_caan_id: number;
  ca_an: string;
  fo_monan_chitiet_id: number[];
  order_idx?: number;
  dm_truong_id: number;
  ten_truong: string;
  truong_list?: IFoCaAnTruong[];
}

interface IMonAn {
  id: number;
  ten_mon_an: string;
}

interface IFoThucDonItemRequest {
  dm_coso_id: number;
  dm_truong_id: number;
  from_date: string;
  to_date: string;
}

interface IFoThucDonInsertItemRequest {
  dm_coso_id: number;
  dm_truong_id: number;
  ngay_an: Date;
  ca_an: number;
  fo_monan_chitiet_ids: number[];
}

interface IFoCaAnTruongRequest {
  dm_coso_id: number;
  fo_caan_id?: number;
}

interface IFoCaAnTruong {
  id: number;
  dm_truong_id: number;
  dm_coso_id: number;
  fo_caan_id: number;
  ca_an: string;
  ten_truong: string;
  order_idx: number;
}

// Hooks
const useMonAn = () => {
  const [monAnMap, setMonAnMap] = useState<Map<number, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const fetchMonAnData = async () => {
    try {
      setIsLoading(true);
      const response = await foMonAnChiTietApi.select_all();
      const newMap = new Map();
      response.data.forEach((monAn: IMonAn) => {
        newMap.set(monAn.id, monAn.ten_mon_an);
      });
      setMonAnMap(newMap);
    } catch (error) {
      console.error("Error fetching món ăn data:", error);
      NotifyHelper.Error("Không thể tải danh sách món ăn");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonAnData();
  }, []);

  const getMonAnName = (monAnId: number): string => {
    const name = monAnMap.get(monAnId);
    return name || '';
  };

  return {
    getMonAnName,
    isLoading,
  };
};

const useCaAnTruong = (dm_coso_id: number) => {
  const [caAnTruongMap, setCaAnTruongMap] = useState<
    Map<string, IFoCaAnTruong[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const fetchCaAnTruongData = async () => {
    try {
      setIsLoading(true);
      const response = await foCaAnTruongApi.select_by_coso(dm_coso_id);
      if (response.is_success) {
        const groupedData = response.data.reduce(
          (acc: Map<string, IFoCaAnTruong[]>, item: IFoCaAnTruong) => {
            const key = item.ca_an;
            if (!acc.has(key)) {
              acc.set(key, []);
            }
            acc.get(key)?.push(item);
            return acc;
          },
          new Map()
        );

        setCaAnTruongMap(groupedData);
        return groupedData; // Trả về data để có thể sử dụng ngay
      }
    } catch (error) {
      console.error("Error fetching ca ăn trường:", error);
      NotifyHelper.Error("Không thể tải thông tin ca ăn trường");
    } finally {
      setIsLoading(false);
    }
  };

  const getTruongByCaAn = useCallback(
    (caAn: string): IFoCaAnTruong[] => {
      return caAnTruongMap.get(caAn) || [];
    },
    [caAnTruongMap]
  );

  // Load data khi component mount hoặc dm_coso_id thay đổi
  useEffect(() => {
    fetchCaAnTruongData();
  }, [dm_coso_id]);

  return {
    caAnTruongMap,
    isLoading,
    getTruongByCaAn,
    fetchCaAnTruongData,
  };
};


// Main Component
const ThucDonPage = () => {
  const [dm_truong_selected_id, setDmTruongSelectedId] = useState<number>(0);

  const { status: thucDonStatus, isShowEditModal } = useSelector(
    (x: RootState) => x.thucDonWrapper.thucDon
  );

  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  const [ngayAnsData, setngayAnsDatas] = useState<INgayAn[]>([]);
  const [showImportThucDon, setShowImportThucDon] = useState<boolean>(false);
  const { getMonAnName } = useMonAn();
  const { getTruongByCaAn, fetchCaAnTruongData } = useCaAnTruong(dm_coso_id);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [dataLoad, setDataLoad] = useState<IFoThucDonItemRequest>({
    dm_coso_id: dm_coso_id,
    dm_truong_id: dm_truong_selected_id,
    from_date: getCurrentDate(),
    to_date: getCurrentDate(),
  });

  const handleDateChange = (field: "from_date" | "to_date") => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setDataLoad((prev) => ({
        ...prev,
        [field]: newValue,
        dm_truong_id: dm_truong_selected_id // Đảm bảo luôn có dm_truong_id mới nhất
      }));
  };

  const mapCaAnWithTruong = (caAn: ICaAn): ICaAn => {
    const truongList = getTruongByCaAn(caAn.ca_an);
    if (truongList.length > 0) {
      const truongNames = truongList.map((t) => t.ten_truong).join(", ");
      return {
        ...caAn,
        dm_truong_id: truongList[0].dm_truong_id,
        ten_truong: truongNames,
        truong_list: truongList,
      };
    }
    return {
      ...caAn,
      dm_truong_id: 0,
      ten_truong: "Chưa phân công",
      truong_list: [],
    };
  };


  const handleAddMonAn = async (
    ngayIndex: number,  
    caIndex: number,
    monAnId: number
  ) => {
    try {
      const caAnInfo = ngayAnsData[ngayIndex].ca_an[caIndex];
  
      if (!dm_truong_selected_id) {
        NotifyHelper.Warning("Vui lòng Chọn cấp học");
        return;
      }
  
      const existingMonAn = caAnInfo.fo_monan_chitiet_id.includes(monAnId);
      if (existingMonAn) {
        NotifyHelper.Warning("Món ăn này đã được chọn");
        return;
      }
  
      const updatedMonAnIds = [...caAnInfo.fo_monan_chitiet_id, monAnId];
  
      setngayAnsDatas((prevData) => {
        const newData = [...prevData];
        newData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id = updatedMonAnIds;
        return newData;
      });
  
      // Chỉ thêm cho trường đã chọn
      const request = {
        dm_coso_id: dm_coso_id,
        dm_truong_id: dm_truong_selected_id, // Sử dụng trường đã chọn
        ngay_an: ngayAnsData[ngayIndex].ngay_an,
        ca_an: caAnInfo.fo_caan_id,
        fo_monan_chitiet_ids: updatedMonAnIds,
      };
  
      const result = await foThucDonApi.insert_multiple(request);
  
      if (!result.is_success) {
        setngayAnsDatas((prevData) => {
          const newData = [...prevData];
          newData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id = 
            newData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id.filter(id => id !== monAnId);
          return newData;
        });
        NotifyHelper.Error("Có lỗi xảy ra khi thêm món ăn");
        return;
      }
  
      NotifyHelper.Success("Thêm món ăn thành công");
    } catch (error) {
      console.error("Error adding món ăn:", error);
      NotifyHelper.Error("Có lỗi xảy ra khi thêm món ăn");
    }
  };

  const handleRemoveMonAn = async (
    ngayIndex: number,
    caIndex: number, 
    monAnIndex: number
  ) => {
    if (await confirm({
      content: translate("Base.Label.DeleteConfirm.Content"),
      title: translate("Base.Label.Confirm"), 
      cancelButtonContent: translate("Base.Label.Close"),
      confirmButtonContent: translate("Base.Label.Delete"),
      confirmButtonType: "danger",
    })) {
      try {
        const caAnInfo = ngayAnsData[ngayIndex].ca_an[caIndex];
        const currentMonAnIds = [...caAnInfo.fo_monan_chitiet_id];
        currentMonAnIds.splice(monAnIndex, 1);
  
        setngayAnsDatas((prevData) => {
          const newData = [...prevData];
          newData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id = currentMonAnIds;
          return newData;
        });
  
        const request = {
          dm_coso_id: dm_coso_id,
          dm_truong_id: dm_truong_selected_id,
          ngay_an: ngayAnsData[ngayIndex].ngay_an,
          ca_an: caAnInfo.fo_caan_id,
          fo_monan_chitiet_ids: currentMonAnIds,
        };
  
        const result = await foThucDonApi.insert_multiple(request);
  
        if (!result.is_success) {
          setngayAnsDatas((prevData) => {
            const newData = [...prevData];
            newData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id = [
              ...ngayAnsData[ngayIndex].ca_an[caIndex].fo_monan_chitiet_id,
            ];
            return newData;
          });
          NotifyHelper.Error("Có lỗi xảy ra khi xóa món ăn");
          return;
        }
  
        NotifyHelper.Success("Xóa món ăn thành công");
  
      } catch (error) {
        console.error("Error removing món ăn:", error);
        NotifyHelper.Error("Có lỗi xảy ra khi xóa món ăn"); 
      }
    }
  };
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("vi-VN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  const fetchThucDonData = async (requestData: IFoThucDonItemRequest) => {
    const res = await foThucDonApi.select_detail_daybyday(requestData);
    return res;
  };
  
  const handleReload = async () => {
    try {
      // Cập nhật state loading nếu cần
      const currentDataLoad = {
        ...dataLoad,
        dm_truong_id: dm_truong_selected_id
      };
  
      const res = await fetchThucDonData(currentDataLoad);
      if (res.is_success && res.data?.ngay_ans?.length > 0) {
        const caAnTruongResponse = await foCaAnTruongApi.select_by_coso(dm_coso_id);
  
        if (caAnTruongResponse.is_success) {
          const ngayAnsWithSchool: INgayAn[] = res.data.ngay_ans.map(
            (ngayAn: INgayAn) => ({
              ...ngayAn,
              ca_an: ngayAn.ca_an
                .map((ca) => {
                  const truongList = caAnTruongResponse.data.filter(
                    (t: IFoCaAnTruong) => t.ca_an === ca.ca_an
                  );
                  return {
                    ...ca,
                    dm_truong_id:
                      truongList.length > 0 ? truongList[0].dm_truong_id : undefined,
                    truong_list: truongList,
                  };
                })
                .filter((ca) => 
                  ca.truong_list?.some((t: IFoCaAnTruong) => 
                    t.dm_truong_id === dm_truong_selected_id
                  )
                )
            })
          );
  
          const filteredNgayAns: INgayAn[] = ngayAnsWithSchool.filter(
            (ngayAn: INgayAn) => ngayAn.ca_an.length > 0
          );
          
          setngayAnsDatas(filteredNgayAns);
        }
      } else {
        setngayAnsDatas([]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      NotifyHelper.Error("Có lỗi xảy ra khi tải dữ liệu");
    }
  };
  
  useEffect(() => {
    setDataLoad(prev => {
      const newDataLoad = {
        ...prev,
        dm_truong_id: dm_truong_selected_id
      };
      handleReload();
      return newDataLoad;
    });
  }, [dm_truong_selected_id]);

  const handleTruongChange = (value: number) => {
    setDmTruongSelectedId(value);
    setDataLoad(prev => ({
      ...prev,
      dm_truong_id: value
    }));
  };
  

  useEffect(() => {
    if (
      thucDonStatus === ePageBaseStatus.is_not_initialization ||
      thucDonStatus === ePageBaseStatus.is_need_reload
    ) {
      handleReload();
    }
  }, [thucDonStatus]);

  const handleBtnImportClicked = () => {
    setShowImportThucDon(true);
  };

  const handleCloseDetailModal = useCallback(() => {
    setShowImportThucDon(false);
  }, []);

  useEffect(() => {
    handleReload();
  }, [dataLoad.from_date, dataLoad.to_date, dm_truong_selected_id]);

  return (
    <Box sx={{ p: 3, margin: "0 auto" }}>
      {/* Header Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Heading
            sx={{
              fontSize: "24px",
              fontWeight: "600",
              color: "fg.default",
              mb: 1,
              letterSpacing: "-0.5px",
            }}
          >
            <MyText text="Quản lý thực đơn" />
          </Heading>
          <MyText
            text="Quản lý thực đơn theo ngày và ca ăn cho các trường"
            sx={{
              color: "fg.muted",
              fontSize: "14px",
              lineHeight: 1.4,
            }}
          />
        </Box>

        {/* Filter Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            bg: "canvas.subtle",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "border.default",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ display: "flex", gap: 4, alignItems: "flex-end" }}>
            <Box>
              <MyText
                text="Từ ngày"
                sx={{
                  mb: 1,
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "fg.default",
                  mr: 2,
                }}
              />
              <MyTextInput
                type="date"
                sx={{
                  width: "180px",
                  height: "36px",
                  borderRadius: "6px",
                  border: "1px solid",
                  borderColor: "border.default",
                  "&:focus": {
                    borderColor: "accent.emphasis",
                    boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)",
                  },
                }}
                value={dataLoad.from_date}
                onChange={handleDateChange("from_date")}
              />
            </Box>
            <Box>
              <MyText
                text="Đến ngày"
                sx={{
                  mb: 1,
                  fontWeight: "600",
                  fontSize: "13px",
                  color: "fg.default",
                  mr: 2,
                }}
              />
              <MyTextInput
                type="date"
                sx={{
                  width: "180px",
                  height: "36px",
                  borderRadius: "6px",
                  border: "1px solid",
                  borderColor: "border.default",
                  "&:focus": {
                    borderColor: "accent.emphasis",
                    boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)",
                  },
                }}
                value={dataLoad.to_date}
                onChange={handleDateChange("to_date")}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
          <Button
              id="btnRefresh"
              leadingVisual={SyncIcon}
              variant="default"
              size="medium"
              sx={{
                fontWeight: "500",
                borderRadius: "6px",
              }}
              onClick={handleReload}
            >
              <MyText text="Base.Label.Refresh" />
            </Button>
            <ComboboxTruong
              value={dm_truong_selected_id}
              onValueChanged={handleTruongChange}

            />
           
            <Button
              id="btnImport"
              leadingVisual={UploadIcon}
              variant="primary"
              size="medium"
              disabled={dm_truong_selected_id === 0} 
              sx={{
                fontWeight: "500",
                borderRadius: "6px",
              }}
              onClick={handleBtnImportClicked}
            >
              <MyText text="Base.Label.Import" />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: "8px",
          height: "calc(100vh - 320px)",
          overflowY: "auto",
          bg: "canvas.default",
        }}
      >
        {thucDonStatus === ePageBaseStatus.is_loading ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              color: "fg.muted",
            }}
          >
            <MyText text="Base.Label.Loading" />
          </Box>
        ) : ngayAnsData?.length > 0 ? (
          <Box sx={{ p: 3 }}>
            {ngayAnsData.map((ngayAn: INgayAn, ngayIndex: number) => (
              <Box
                key={ngayIndex}
                sx={{
                  mb: 4,
                  "&:last-child": { mb: 0 },
                }}
              >
                {/* Date Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3,
                    pb: 2,
                    borderBottom: "1px solid",
                    borderColor: "border.muted",
                  }}
                >
                  <Box
                    sx={{
                      color: "accent.fg",
                      bg: "accent.subtle",
                      p: 1.5,
                      borderRadius: "8px",
                    }}
                  >
                    <CalendarIcon size={20} />
                  </Box>
                  <Heading
                    sx={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "fg.default",
                    }}
                  >
                    <MyText text={formatDate(ngayAn.ngay_an)} />
                  </Heading>
                </Box>

                {/* Meals Grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: [
                      "1fr",
                      null,
                      "repeat(3, 1fr)",
                      "repeat(4, 1fr)",
                    ],
                    gap: 3,
                    overflowX: "auto", // Cho phép cuộn ngang
                    width: "100%",
                  }}
                >
                  {ngayAn.ca_an
                    .sort((a, b) => (a.order_idx || 0) - (b.order_idx || 0))
                    .map((ca: ICaAn, caIndex: number) => (
                      <Box
                        key={caIndex}
                        sx={{
                          bg: "canvas.default",
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: "border.default",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {/* Meal Header */}
                        <Box
                          sx={{
                            p: 3,
                            borderBottom: "1px solid",
                            borderColor: "border.default",
                            bg: "canvas.subtle",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb:0,
                            }}
                          >
                            <Box
                              sx={{
                                bg: "success.subtle",
                                p: 1.5,
                                borderRadius: "6px",
                                color: "success.fg",
                              }}
                            >
                              <StackIcon size={16} />
                            </Box>
                            <MyText
                              text={ca.ca_an}
                              sx={{
                                fontWeight: "600",
                                fontSize: "16px",
                                color: "fg.default",
                              }}
                            />
                          </Box>

                          {/* Schools Badges */}
                          {/* <Box
                            sx={{
                              height: 100,
                              overflowY: "auto",
                              "&::-webkit-scrollbar": {
                                width: "3px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                background: "var(--color-border-default)",
                                borderRadius: "2px",
                              },
                            }}
                          >
                            {ca.truong_list && ca.truong_list.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                  p: 2,
                                }}
                              >
                                {ca.truong_list.map((truong, idx) => {
                                  const variants: LabelColorOptions[] = [
                                    "accent",
                                    "success",
                                    "done",
                                    "sponsors",
                                  ];
                                  const variant =
                                    variants[idx % variants.length];

                                  return (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 2,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 2,
                                          height: 2,
                                          borderRadius: "50%",
                                          bg:
                                            variant === "accent"
                                              ? "accent.fg"
                                              : variant === "success"
                                              ? "success.fg"
                                              : variant === "done"
                                              ? "done.fg"
                                              : "sponsors.fg",
                                          flexShrink: 0,
                                          marginTop: "6px",
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "var(--color-fg-default)",
                                          fontWeight: 500,
                                        }}
                                      >
                                        {truong.ten_truong}
                                      </span>
                                    </Box>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  p: 2,
                                  color: "fg.muted",
                                  fontSize: "12px",
                                  fontStyle: "italic",
                                }}
                              >
                                Chưa phân công trường
                              </Box>
                            )}
                          </Box> */}
                        </Box>

                        {/* Dishes Section */}
                        <Box
                          sx={{
                            p: 3,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            height: "350px",
                          }}
                        >
                          {/* Header với số lượng món ăn */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 3,
                            }}
                          >
                            <MyText
                              text="Danh sách món ăn"
                              sx={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "fg.default",
                              }}
                            />
                            <MyText
                              text={`${ca.fo_monan_chitiet_id.filter(monAnId => getMonAnName(monAnId)).length} món`}
                              sx={{
                                fontSize: "12px",
                                color: "fg.muted",
                              }}
                            />
                          </Box>

                          {/* Danh sách món ăn */}
                          <Box
                            sx={{
                              flex: 1,
                              overflowY: "auto",
                              mb: 3,
                              "&::-webkit-scrollbar": {
                                width: "4px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                background: "border.default",
                                borderRadius: "2px",
                              },
                            }}
                          >
                            {ca.fo_monan_chitiet_id.filter(monAnId => getMonAnName(monAnId)).length > 0 ? (
                              <ActionList
                                role="menu"
                                showDividers
                                aria-label="Danh sách món ăn"
                              >
                                <ActionList.Group>
                                  {ca.fo_monan_chitiet_id
                                    .filter(monAnId => getMonAnName(monAnId))
                                    .map((monAnId, index) => (
                                      <ActionList.Item
                                        key={index}
                                        sx={{
                                          "&:hover": {
                                            bg: "canvas.subtle",
                                          },
                                        }}
                                      >
                                        <ActionList.LeadingVisual>
                                          <Box
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              borderRadius: "4px",
                                              bg: "canvas.subtle",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              fontSize: "12px",
                                              color: "fg.muted",
                                            }}
                                          >
                                            {index + 1}
                                          </Box>
                                        </ActionList.LeadingVisual>

                                        <MyText
                                          text={getMonAnName(monAnId)}
                                          sx={{
                                            fontSize: "13px",
                                            color: "fg.default",
                                          }}
                                        />

                                        <ActionList.TrailingVisual>
                                          <Button
                                            variant="invisible"
                                            size="small"
                                            onClick={() =>
                                              handleRemoveMonAn(
                                                ngayIndex,
                                                caIndex,
                                                index
                                              )
                                            }
                                            sx={{
                                              color: "fg.muted",
                                              p: 1,
                                              borderRadius: "4px",
                                              minWidth: "24px",
                                              height: "24px",
                                              "&:hover": {
                                                color: "danger.fg",
                                                bg: "danger.subtle",
                                              },
                                            }}
                                          >
                                            ×
                                          </Button>
                                        </ActionList.TrailingVisual>
                                      </ActionList.Item>
                                    ))}
                                </ActionList.Group>
                              </ActionList>
                            ) : (
                              <Box
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 3,
                                  borderRadius: "6px",
                                  bg: "canvas.subtle",
                                }}
                              >
                                <MyText
                                  text="Chưa có món ăn"
                                  sx={{
                                    color: "fg.muted",
                                    fontSize: "13px",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* Combobox thêm món ăn */}
                          <ComboboxMonAn
                            width="100%"
                            value={undefined}
                            onValueChanged={(newValue) =>
                              handleAddMonAn(ngayIndex, caIndex, newValue)
                            }
                            // sx={{
                            //   "& input": {
                            //     height: "32px",
                            //     borderRadius: "6px",
                            //     fontSize: "13px",
                            //     "&:focus": {
                            //       borderColor: "accent.emphasis",
                            //       boxShadow: "0 0 0 1px rgba(3, 102, 214, 0.2)"
                            //     }
                            //   }
                            // }}
                          />
                        </Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              color: "fg.muted",
            }}
          >
            <MyText text="Vui lòng chọn cấp học" />
          </Box>
        )}
      </Box>

      {/* Modals */}
      {isShowEditModal && <ThucDonEditModal />}
      {showImportThucDon && (
        <ThucDonImport
          onClose={handleCloseDetailModal}
          animationOf={`#btnImport`}
          dm_truong_id={dm_truong_selected_id}  
        />
      )}
    </Box>
  );
};

export default ThucDonPage;